"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { EVENTS_MONTHS } from "@/design-data/marketing-data";
import type { EventIndexItem, EventSeason } from "@/greenpak/events-index";

type Season = (typeof EVENTS_MONTHS)[number]["key"];

/**
 * What each season is for, and the area guides at their best then — the page
 * the homepage season cards open (they used to open one province page each:
 * Winter, whose text names Lahore and the Punjab desert forts, opened
 * Balochistan).
 */
const SEASON_NOTES: Record<EventSeason, { months: string; body: string; areas: { slug: string; name: string }[] }> = {
  spring: {
    months: "March–May",
    body: "Blossom weeks in Hunza and Skardu, the Kalasha spring festival, Nowruz, and the last mild weeks in the plains.",
    areas: [
      { slug: "hunza", name: "Hunza" },
      { slug: "skardu", name: "Skardu" },
      { slug: "kalasha-valleys", name: "Kalasha Valleys" },
      { slug: "chitral", name: "Chitral" },
      { slug: "lahore", name: "Lahore" },
    ],
  },
  summer: {
    months: "June–August",
    body: "The high season in the north: polo at Shandur, the passes and trek routes open, harvest rites in Hunza and the Kalasha valleys.",
    areas: [
      { slug: "chitral", name: "Chitral" },
      { slug: "hunza", name: "Hunza" },
      { slug: "skardu", name: "Skardu" },
      { slug: "naran-kaghan", name: "Naran & Kaghan" },
      { slug: "fairy-meadows", name: "Fairy Meadows" },
      { slug: "neelum-valley", name: "Neelum Valley" },
    ],
  },
  autumn: {
    months: "September–November",
    body: "Clear skies and golden poplars up north, desert rallies, Lok Mela in Islamabad and the Gurpurab pilgrimage at Nankana Sahib.",
    areas: [
      { slug: "hunza", name: "Hunza" },
      { slug: "khaplu-ghanche", name: "Khaplu & Ghanche" },
      { slug: "skardu", name: "Skardu" },
      { slug: "nankana-sahib", name: "Nankana Sahib" },
      { slug: "islamabad", name: "Islamabad" },
    ],
  },
  winter: {
    months: "December–February",
    body: "The plains and the coast at their best: Lahore, the Cholistan desert rally, the Makran coast and Sindh's heritage; solstice fires and Chawmos up north.",
    areas: [
      { slug: "lahore", name: "Lahore" },
      { slug: "bahawalpur-cholistan", name: "Bahawalpur & Cholistan" },
      { slug: "makran-coast", name: "Makran Coast" },
      { slug: "thatta-makli", name: "Thatta & Makli" },
      { slug: "karachi", name: "Karachi" },
      { slug: "kalasha-valleys", name: "Kalasha Valleys" },
    ],
  },
};

export function EventsContent({ events, initialSeason = "all" }: { events: EventIndexItem[]; initialSeason?: Season }) {
  const [month, setMonth] = useState<Season>(initialSeason);

  // Calendar order, January first, whatever the CMS sort order; winter runs
  // December, January, February.
  const filtered = useMemo(() => {
    const key = (m: number) => (month === "winter" && m === 12 ? 0 : m);
    return (month === "all" ? events : events.filter((e) => e.season === month)).slice().sort((a, b) => key(a.monthOrder) - key(b.monthOrder));
  }, [events, month]);

  const featured = useMemo(
    () =>
      [...events]
        .sort((a, b) => a.monthOrder - b.monthOrder)
        .filter((e) => ["shandur-polo", "chilam-joshi", "sibi-mela", "basant-jashn-e-baharan", "urs-lal-shahbaz-qalandar"].includes(e.slug))
        .slice(0, 5),
    [events],
  );
  const note = month === "all" ? null : SEASON_NOTES[month];

  return (
    <>
      <section className="pbd-container pt-16 pb-2">
        <p className="m-0 text-sm text-[var(--muted)]">
          <Link href="/" className="text-[var(--muted)] no-underline hover:text-[var(--accent)]">
            Home
          </Link>{" "}
          <span className="opacity-50">/</span> <span className="text-[var(--accent)]">Events</span>
        </p>
        <h1 className="mt-3.5 mb-0 font-[family-name:var(--font-display)] text-[clamp(2.4rem,5vw,4rem)] font-medium leading-[1.02] tracking-tight">
          Festivals &amp; Seasons
        </h1>
        <p className="mt-4 mb-0 max-w-[600px] text-[1.05rem] leading-[1.7] text-[var(--muted)]">
          Polo at Shandur, Kalasha rites, blossom weeks, Sufi urs, and desert fairs, {events.length} events to plan around across Pakistan.
        </p>
      </section>

      {featured.length > 0 ? (
        <section className="pbd-container pb-4 pt-4">
          <p className="pbd-eyebrow mb-4">Don&apos;t miss</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {featured.map((e) => (
              <Link
                key={e.slug}
                href={`/events/${e.slug}`}
                className="group relative aspect-[4/5] overflow-hidden rounded-lg border border-[var(--border-subtle)] no-underline"
              >
                <Image
                  src={e.image}
                  alt={e.name}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 640px) 50vw, 20vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(3,12,9,0.92)] via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="m-0 text-[10px] font-bold tracking-[0.14em] text-[var(--cta)] uppercase">{e.month}</p>
                  <h3 className="mt-1 mb-0 font-[family-name:var(--font-display)] text-[1.05rem] font-medium leading-snug text-white">
                    {e.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section id="calendar" className="pbd-container scroll-mt-28 pt-6 pb-2">
        <div className="flex flex-wrap gap-2">
          {EVENTS_MONTHS.map((m) => {
            const on = month === m.key;
            const count =
              m.key === "all" ? events.length : events.filter((e) => e.season === (m.key as EventSeason)).length;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => {
                  setMonth(m.key);
                  // Keep the address shareable: /events?season=winter.
                  window.history.replaceState(null, "", m.key === "all" ? "/events" : `/events?season=${m.key}`);
                }}
                className={`cursor-pointer rounded-full border px-4 py-2 text-[13px] font-semibold font-[inherit] ${
                  on
                    ? "border-[var(--cta)] bg-[var(--cta)] text-[#2b2b2b]"
                    : "border-[var(--border-subtle)] bg-transparent text-[var(--muted)]"
                }`}
              >
                {m.label}
                <span className="ml-1.5 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </section>

      {note ? (
        <section className="pbd-container pt-6">
          <div className="rounded-xl border border-[var(--border-subtle)] p-6">
            <p className="m-0 text-[11px] font-bold tracking-[0.16em] text-[var(--cta)] uppercase">{note.months}</p>
            <p className="mt-2 mb-0 max-w-[760px] text-[1.02rem] leading-[1.7]">{note.body}</p>
            <p className="mt-4 mb-0 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <span className="text-[var(--muted)]">At its best now:</span>
              {note.areas.map((a) => (
                <Link key={a.slug} href={`/areas/${a.slug}`} className="font-semibold text-[var(--accent)] no-underline hover:underline">
                  {a.name}
                </Link>
              ))}
            </p>
          </div>
        </section>
      ) : null}

      <section className="pbd-container pb-20 pt-6">
        <div className="overflow-hidden rounded-xl border border-[var(--border-subtle)]">
          {filtered.map((e, i) => (
            <Link
              key={e.slug}
              href={`/events/${e.slug}`}
              className={`grid gap-4 border-b border-[var(--border-soft)] px-6 py-5 text-[var(--foreground)] no-underline transition last:border-b-0 hover:bg-[color-mix(in_srgb,var(--accent)_6%,transparent)] max-[640px]:grid-cols-1 sm:grid-cols-[140px_1fr_auto] ${
                i % 2 ? "bg-[color-mix(in_srgb,var(--surface)_30%,transparent)]" : "bg-transparent"
              }`}
            >
              <span className="text-sm font-semibold text-[var(--accent)]">{e.month}</span>
              <div>
                <h3 className="m-0 font-[family-name:var(--font-display)] text-[1.2rem] font-medium">{e.name}</h3>
                <p className="mt-1 mb-0 text-[13px] leading-relaxed text-[var(--muted)]">{e.blurb}</p>
              </div>
              <span className="self-center text-sm font-semibold tracking-wide text-[var(--muted)] uppercase">{e.region}</span>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
